import { DataStoreId, IAggregateStoreAlternativeInfo } from "c8-platform";
import { action, computed, IObservableArray, makeObservable, observable } from "mobx";
import { AggregateStoreGroupStore } from "./AggregateStoreGroupStore";
import { AggregateStoreInfoStore } from "./AggregateStoreInfoStore";
import { compact } from "lodash";

class AggregateStoreAlternativeStore {
	/** reference to root store */
	readonly aggregateInfoStore: AggregateStoreInfoStore;

	/** condition expression */
	@observable condition = "";
	
	/** description of alternative */
	@observable description = "";
	
	/** 
	 * indicates this is a single alternative created to represent a dynamic aggregate store; in this case we do
	 * not know all the actual OOTB alternatives, but rather we allow the user to configure additional queries to
	 * execute per aggregate store group. This one alternative will manage all such groups.
	 */
	readonly dynamic: boolean;
	
	/** groups for alternative */
	readonly groups: IObservableArray<AggregateStoreGroupStore>;
	
	/** value representing all editable settings in store */
	@computed get alternativeValue(): IAggregateStoreAlternativeInfo {
		const {
			condition = "",
			description = "",
			groups = [],
		} = this;

		return {
			condition,
			description,
			groups: groups.map((group) => group.groupValue),
		};
	}

	/** return true if able to add a new group - allow at most 3 groups to protect performance */
	get canAddGroup(): boolean {
		const { aggregateInfoStore, groups, isValid } = this;
		return !aggregateInfoStore.readOnly && isValid && (groups.length < 3);
	}

	/** return true if able to remove this alternative */
	get canRemoveAlternative(): boolean {
		const { isConditionalAggregateStore, overridden } = this.aggregateInfoStore;
		return overridden && isConditionalAggregateStore;
	}
	
	/** return true if condition is required for alternative */
	get conditionError(): string | undefined {
		if (!this.conditionIsValid) {
			return this.aggregateInfoStore.requiredValueError;
		}
	}

	/** return true if condition is required for alternative and not populated */
	get conditionIsValid(): boolean {
		return !this.isConditionRequired || !!this.condition;
	}

	/** return all data store ids used in alternative */
	get dataStoreIds(): DataStoreId[] {
		return compact(this.groups.map((group) => group.groupInfo.dataStoreId));
	}

	/** return true if condition is required for alternative */
	get isConditionRequired(): boolean {
		const { alternativeRequiresCondition, overridden } = this.aggregateInfoStore;
		return overridden && alternativeRequiresCondition(this);
	}

	/** return true if all data entered for alternative is valid */
	get isValid(): boolean {
		const {
			conditionIsValid,
			groups,
		} = this;

		return conditionIsValid && groups.every((group) => group.isValid);
	}

	/** update condition */
	@action onChangeCondition = (value: string): void => {
		this.condition = value;
	};

	/** update description */
	@action onChangeDescription = (value: string): void => {
		this.description = value;
	};

	/** add new group to an alternative */
	@action onClickAddGroup = (): void => {
		const { aggregateInfoStore, groups } = this;

		groups.push(new AggregateStoreGroupStore({
			nodeType: aggregateInfoStore.targetClass,
			notFirstOfType: true,
		}, this));

		aggregateInfoStore.resetValidation();
	};

	/** remove this alternative */
	@action onClickRemoveAlternative = (): void => {
		this.aggregateInfoStore.removeAlternative(this);
	};

	constructor(info: IAggregateStoreAlternativeInfo, aggregateInfoStore: AggregateStoreInfoStore) {
		const { condition, description, dynamic, groups } = info;
		
		this.aggregateInfoStore = aggregateInfoStore;
		this.condition = condition;
		this.description = description;
		this.dynamic = dynamic;
		this.groups = observable.array<AggregateStoreGroupStore>(groups.map((group) => new AggregateStoreGroupStore(group, this)));
		makeObservable(this);
	}
}

export default AggregateStoreAlternativeStore;